import React, { forwardRef } from "react";
import Select, {
  ActionMeta,
  StylesConfig,
  components,
  GroupBase,
  InputActionMeta,
  MenuListProps,
  MultiValue,
  MultiValueGenericProps,
  OptionProps,
  ValueContainerProps,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  ListRowProps,
} from "react-virtualized";
import { CheckIcon, MinusIcon } from "lucide-react";
import { Label } from "../ui/label";
import {
  CustomMultiselectProps,
  SelectableOption,
  SelectAllContextValue,
} from "./type";
import {
  VIRTUALIZED_MENU_MAX_HEIGHT,
  VIRTUALIZED_MENU_MIN_ROW_HEIGHT,
  VIRTUALIZED_MENU_OVERSCAN,
} from "@/shared/constants/virtualizedDefaultValues";
import CustomToolTip from "./ToolTip";
import {
  MIN_MULTISELECT_INPUT_WIDTH,
  DEFAULT_VALUE_CONTAINER_GAP,
  MIN_TRUNCATED_CHIP_WIDTH,
  OVERFLOW_BADGE_VISIBLE_CLASS_NAME,
} from "@shared/constants/common.constants";

const LimitedValueContainer = <Option, IsMulti extends boolean>(
  props: ValueContainerProps<Option, IsMulti>,
): React.ReactElement => {
  const selectedValues = props.getValue();
  const getOptionLabelFn = props.selectProps.getOptionLabel;
  const rawSelectionLabels = selectedValues.map((option) =>
    getOptionLabelFn(option),
  );
  const selectionSignature = rawSelectionLabels.join("\u0000");
  const prevSignatureRef = React.useRef<string>(selectionSignature);
  const selectionLabelsRef = React.useRef<string[]>(rawSelectionLabels);

  if (
    prevSignatureRef.current !== selectionSignature ||
    selectionLabelsRef.current.length !== rawSelectionLabels.length
  ) {
    prevSignatureRef.current = selectionSignature;
    selectionLabelsRef.current = rawSelectionLabels;
  }
  const selectionLabels = selectionLabelsRef.current;

  const overflowBadgeRef = React.useRef<HTMLSpanElement>(null);
  const customChipExtraWidthRef = React.useRef<number>(0);
  const chipWidthCacheRef = React.useRef<Map<string, number>>(new Map());
  const badgeWidthCacheRef = React.useRef<Map<number, number>>(new Map());
  const [visibleCount, setVisibleCount] = React.useState<number>(0);

  const measureFit = React.useCallback((): void => {
    if (!props.isMulti) {
      return;
    }

    const totalSelected = selectionLabels.length;
    if (totalSelected === 0) {
      setVisibleCount((prev) => (prev === 0 ? prev : 0));
      return;
    }

    const badgeEl = overflowBadgeRef.current;
    const valueContainerEl =
      badgeEl?.closest<HTMLElement>(".custom-multiselect__value-container") ??
      badgeEl?.parentElement ??
      null;
    if (!valueContainerEl || typeof window === "undefined") {
      return;
    }

    const controlEl = valueContainerEl.closest<HTMLElement>(
      ".custom-multiselect__control",
    );
    const indicatorsEl = controlEl?.querySelector<HTMLElement>(
      ".custom-multiselect__indicators",
    );
    const indicatorsWidth = indicatorsEl
      ? Math.ceil(
          indicatorsEl.getBoundingClientRect().width ||
            indicatorsEl.offsetWidth ||
            0,
        )
      : 0;
    const controlClientWidth =
      controlEl?.clientWidth ||
      Math.floor(controlEl?.getBoundingClientRect().width ?? 0);
    const containerClientWidth =
      valueContainerEl.clientWidth ||
      Math.floor(valueContainerEl.getBoundingClientRect().width);
    const valueContainerBoxWidth =
      controlClientWidth > 0
        ? Math.max(0, controlClientWidth - indicatorsWidth)
        : containerClientWidth;

    if (valueContainerBoxWidth <= 0) {
      return;
    }

    const computedStyle = window.getComputedStyle(valueContainerEl);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const gap =
      parseFloat(computedStyle.columnGap || computedStyle.gap) ||
      DEFAULT_VALUE_CONTAINER_GAP;
    const availableContentWidth = Math.max(
      0,
      valueContainerBoxWidth - paddingLeft - paddingRight,
    );

    if (availableContentWidth <= 0) {
      setVisibleCount((prev) => (prev === 0 ? prev : 0));
      return;
    }

    const renderedChips = Array.from(
      valueContainerEl.querySelectorAll<HTMLElement>(
        ":scope > .custom-multiselect__multi-value",
      ),
    );

    const chipWidthCache = chipWidthCacheRef.current;
    const badgeWidthCache = badgeWidthCacheRef.current;
    const chipWidths: number[] = new Array<number>(totalSelected);

    const measureElementWidth = (element: HTMLElement): number =>
      Math.ceil(
        element.getBoundingClientRect().width || element.offsetWidth || 0,
      );

    const createChipProbe = (labelText: string): HTMLDivElement => {
      const chipProbe = document.createElement("div");
      chipProbe.className = "custom-multiselect__multi-value";
      chipProbe.style.cssText =
        "display:flex;align-items:center;box-sizing:border-box;flex-shrink:0;max-width:none;width:auto;";

      const chipLabelProbe = document.createElement("div");
      chipLabelProbe.className = "custom-multiselect__multi-value__label";
      chipLabelProbe.style.cssText = "white-space:nowrap;box-sizing:border-box;";
      chipLabelProbe.textContent = labelText;

      const chipRemoveProbe = document.createElement("div");
      chipRemoveProbe.className = "custom-multiselect__multi-value__remove";
      chipRemoveProbe.style.cssText =
        "display:flex;align-items:center;box-sizing:border-box;flex-shrink:0;";

      chipProbe.appendChild(chipLabelProbe);
      chipProbe.appendChild(chipRemoveProbe);
      return chipProbe;
    };

    const uncachedIndices: number[] = [];
    for (let i = 0; i < totalSelected; i += 1) {
      const cached = chipWidthCache.get(selectionLabels[i] ?? "");
      if (cached !== undefined && cached > 0 && !renderedChips[i]) {
        chipWidths[i] = cached;
      } else {
        uncachedIndices.push(i);
      }
    }

    const uncachedBadgeCounts: number[] = [];
    for (let hidden = 1; hidden <= totalSelected; hidden += 1) {
      if (!badgeWidthCache.has(hidden)) {
        uncachedBadgeCounts.push(hidden);
      }
    }

    if (uncachedIndices.length > 0 || uncachedBadgeCounts.length > 0) {
      const probeContainer = document.createElement("div");
      probeContainer.setAttribute("aria-hidden", "true");
      probeContainer.style.cssText =
        "position:fixed;top:-9999px;left:-9999px;visibility:hidden;pointer-events:none;display:flex;flex-wrap:nowrap;align-items:center;white-space:nowrap;";

      try {
        let baseChip0Probe: HTMLDivElement | null = null;
        let clone0: HTMLElement | null = null;

        if (renderedChips.length > 0 && uncachedIndices.length > 0) {
          baseChip0Probe = createChipProbe(selectionLabels[0] ?? "");
          clone0 = renderedChips[0].cloneNode(true) as HTMLElement;
          clone0.style.cssText =
            "display:flex;align-items:center;box-sizing:border-box;flex-shrink:0;max-width:none;width:auto;";
          probeContainer.appendChild(baseChip0Probe);
          probeContainer.appendChild(clone0);
        }

        const cloneProbes = new Map<number, HTMLElement>();
        const fallbackProbes = new Map<number, HTMLDivElement>();

        for (const index of uncachedIndices) {
          const labelText = selectionLabels[index] ?? "";
          const renderedChip = renderedChips[index];
          if (renderedChip) {
            const clone = renderedChip.cloneNode(true) as HTMLElement;
            clone.style.cssText =
              "display:flex;align-items:center;box-sizing:border-box;flex-shrink:0;max-width:none;width:auto;";
            probeContainer.appendChild(clone);
            cloneProbes.set(index, clone);
          }
          const fallbackProbe = createChipProbe(labelText);
          probeContainer.appendChild(fallbackProbe);
          fallbackProbes.set(index, fallbackProbe);
        }

        const badgeProbes = new Map<number, HTMLSpanElement>();
        for (const hidden of uncachedBadgeCounts) {
          const badgeProbe = document.createElement("span");
          badgeProbe.className = OVERFLOW_BADGE_VISIBLE_CLASS_NAME;
          badgeProbe.textContent = `+${hidden}`;
          probeContainer.appendChild(badgeProbe);
          badgeProbes.set(hidden, badgeProbe);
        }

        valueContainerEl.appendChild(probeContainer);

        if (baseChip0Probe && clone0) {
          const baseChip0Width = measureElementWidth(baseChip0Probe);
          const renderedChip0Width = measureElementWidth(clone0);
          if (renderedChip0Width > 0 && baseChip0Width > 0) {
            const nextExtra = Math.max(0, renderedChip0Width - baseChip0Width);
            if (nextExtra !== customChipExtraWidthRef.current) {
              customChipExtraWidthRef.current = nextExtra;
              chipWidthCache.clear();
            }
          }
        }

        for (const index of uncachedIndices) {
          const labelText = selectionLabels[index] ?? "";
          const existingCached = chipWidthCache.get(labelText);
          const clone = cloneProbes.get(index);
          if (existingCached !== undefined && existingCached > 0 && !clone) {
            chipWidths[index] = existingCached;
            continue;
          }

          let measuredWidth = 0;
          if (clone) {
            measuredWidth = measureElementWidth(clone);
          }
          if (measuredWidth <= 0) {
            const fallbackProbe = fallbackProbes.get(index);
            if (fallbackProbe) {
              measuredWidth =
                measureElementWidth(fallbackProbe) +
                customChipExtraWidthRef.current;
            }
          }

          chipWidths[index] = measuredWidth;
          if (measuredWidth > 0) {
            chipWidthCache.set(labelText, measuredWidth);
          }
        }

        for (const [hidden, badgeProbe] of badgeProbes.entries()) {
          const measuredBadge = measureElementWidth(badgeProbe);
          if (measuredBadge > 0) {
            badgeWidthCache.set(hidden, measuredBadge);
          }
        }
      } finally {
        probeContainer.remove();
      }
    }

    const getOverflowBadgeWidth = (hidden: number): number =>
      badgeWidthCache.get(hidden) ?? 0;

    const prefixSums: number[] = new Array<number>(totalSelected + 1).fill(0);
    for (let i = 0; i < totalSelected; i += 1) {
      prefixSums[i + 1] = prefixSums[i] + chipWidths[i];
    }

    let nextVisibleCount = 0;
    for (let count = totalSelected; count >= 1; count -= 1) {
      const hidden = totalSelected - count;
      const badgeWidth = hidden > 0 ? getOverflowBadgeWidth(hidden) : 0;
      const totalGaps = (hidden > 0 ? count + 1 : count) * gap;
      const requiredWidth =
        prefixSums[count] +
        badgeWidth +
        totalGaps +
        MIN_MULTISELECT_INPUT_WIDTH;

      if (requiredWidth <= availableContentWidth) {
        nextVisibleCount = count;
        break;
      }
    }

    if (nextVisibleCount === 0 && totalSelected > 0) {
      const hiddenIfOneShown = totalSelected - 1;
      const badgeWidthIfOneShown =
        hiddenIfOneShown > 0 ? getOverflowBadgeWidth(hiddenIfOneShown) : 0;
      const gapsIfOneShown = (hiddenIfOneShown > 0 ? 2 : 1) * gap;
      const availableForFirstChip =
        availableContentWidth -
        badgeWidthIfOneShown -
        gapsIfOneShown -
        MIN_MULTISELECT_INPUT_WIDTH;
      const minFirstChipWidth = Math.min(
        chipWidths[0] || MIN_TRUNCATED_CHIP_WIDTH,
        MIN_TRUNCATED_CHIP_WIDTH + customChipExtraWidthRef.current,
      );

      if (availableForFirstChip >= minFirstChipWidth) {
        nextVisibleCount = 1;
      }
    }

    setVisibleCount((prev) =>
      prev === nextVisibleCount ? prev : nextVisibleCount,
    );
  }, [props.isMulti, selectionLabels]);

  React.useLayoutEffect(() => {
    if (!props.isMulti) {
      return;
    }

    measureFit();

    const badgeEl = overflowBadgeRef.current;
    const valueContainerEl =
      badgeEl?.closest<HTMLElement>(".custom-multiselect__value-container") ??
      badgeEl?.parentElement ??
      null;
    if (!valueContainerEl || typeof ResizeObserver === "undefined") {
      return;
    }

    const controlEl = valueContainerEl.closest<HTMLElement>(
      ".custom-multiselect__control",
    );
    let rafId: number | null = null;

    const scheduleMeasure = (): void => {
      if (rafId !== null) {
        return;
      }
      if (
        typeof window !== "undefined" &&
        typeof window.requestAnimationFrame === "function"
      ) {
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          measureFit();
        });
      } else {
        measureFit();
      }
    };

    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(valueContainerEl);
    if (controlEl && controlEl !== valueContainerEl) {
      observer.observe(controlEl);
    }

    return (): void => {
      observer.disconnect();
      if (
        rafId !== null &&
        typeof window !== "undefined" &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };
  }, [measureFit, props.isMulti, selectionSignature, visibleCount]);

  if (!props.isMulti) {
    return <components.ValueContainer {...props} />;
  }

  const children = React.Children.toArray(props.children);
  const inputChild = children[children.length - 1];

  if (!React.isValidElement(inputChild)) {
    return <components.ValueContainer {...props} />;
  }

  const clampedVisibleCount =
    selectedValues.length > 0
      ? Math.min(selectedValues.length, Math.max(0, visibleCount))
      : 0;
  const hiddenCount = Math.max(0, selectedValues.length - clampedVisibleCount);

  const visibleChildren =
    selectedValues.length > 0
      ? children.slice(0, clampedVisibleCount)
      : children.slice(0, -1);
  const stableInput = React.cloneElement(inputChild, {
    key: "multiselect-input",
  });
  const overflowLabel = selectionLabels.slice(clampedVisibleCount).join(", ");

  return (
    <components.ValueContainer {...props}>
      {visibleChildren}
      <CustomToolTip
        side="bottom"
        className="text-[13px]"
        triggerChild={
          <span
            ref={overflowBadgeRef}
            key="multiselect-overflow-indicator"
            aria-hidden={hiddenCount === 0}
            className={
              hiddenCount > 0 ? OVERFLOW_BADGE_VISIBLE_CLASS_NAME : "hidden"
            }
          >
            {hiddenCount > 0 ? `+${hiddenCount}` : null}
          </span>
        }
        triggerContent={overflowLabel}
      />
      {stableInput}
    </components.ValueContainer>
  );
};

const TruncatedMultiValueLabel = <
  Option,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MultiValueGenericProps<Option, IsMulti, Group>,
): React.ReactElement => {
  const labelRef = React.useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);
  const fullLabel = props.selectProps.getOptionLabel(props.data);

  const checkTruncation = React.useCallback((): void => {
    const labelElement = labelRef.current;
    if (!labelElement) {
      setIsTruncated(false);
      return;
    }

    setIsTruncated(labelElement.scrollWidth > labelElement.clientWidth + 1);
  }, []);

  React.useLayoutEffect(() => {
    const labelElement = labelRef.current;
    if (!labelElement) {
      setIsTruncated(false);
      return;
    }

    checkTruncation();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(labelElement);

    return (): void => {
      observer.disconnect();
    };
  }, [checkTruncation, fullLabel, isTruncated, props.children]);

  const labelProps = {
    ...props,
    innerProps: {
      ...props.innerProps,
      ref: labelRef,
    },
  };

  const label = (
    <div className="min-w-0 overflow-hidden" onMouseEnter={checkTruncation}>
      <components.MultiValueLabel {...labelProps}>
        {props.children}
      </components.MultiValueLabel>
    </div>
  );

  return isTruncated ? (
    <CustomToolTip triggerChild={label} triggerContent={fullLabel} />
  ) : (
    label
  );
};

export const MaxValueContainer = <Option, IsMulti extends boolean>(
  props: ValueContainerProps<Option, IsMulti>,
): React.ReactElement => {
  const selectedValues = props.getValue();

  if (selectedValues.length > 1) {
    return (
      <components.ValueContainer {...props}>
        {selectedValues.length} selected
      </components.ValueContainer>
    );
  }

  return <components.ValueContainer {...props} />;
};

const VirtualizedMenuList = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MenuListProps<Option, IsMulti, Group>,
): React.ReactElement => {
  const { children, maxHeight, getValue, innerProps, innerRef, options } =
    props;
  const childArray = React.Children.toArray(children);
  const selectedValue = getValue()[0];
  const selectedIndex =
    selectedValue !== undefined
      ? options.findIndex((option) => option === selectedValue)
      : -1;
  const listHeight = Math.min(maxHeight, VIRTUALIZED_MENU_MAX_HEIGHT);
  const measurementCache = React.useMemo(
    () =>
      new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: VIRTUALIZED_MENU_MIN_ROW_HEIGHT,
        minHeight: VIRTUALIZED_MENU_MIN_ROW_HEIGHT,
      }),
    [],
  );

  React.useEffect(() => {
    measurementCache.clearAll();
  }, [childArray.length, measurementCache, options]);

  if (childArray.length === 0 || childArray.length !== options.length) {
    return <components.MenuList {...props}>{children}</components.MenuList>;
  }

  const rowRenderer = ({
    index,
    key,
    parent,
    style,
  }: ListRowProps): React.ReactElement => (
    <CellMeasurer
      cache={measurementCache}
      columnIndex={0}
      key={key}
      parent={parent}
      rowIndex={index}
    >
      <div style={style}>{childArray[index] as React.ReactElement}</div>
    </CellMeasurer>
  );

  return (
    <div
      {...innerProps}
      ref={innerRef}
      style={{
        maxHeight: listHeight,
        overflow: "hidden",
      }}
    >
      <div style={{ height: listHeight }}>
        <AutoSizer disableHeight>
          {({ width }): React.ReactElement => (
            <List
              deferredMeasurementCache={measurementCache}
              height={listHeight}
              overscanRowCount={VIRTUALIZED_MENU_OVERSCAN}
              rowCount={childArray.length}
              rowHeight={measurementCache.rowHeight}
              rowRenderer={rowRenderer}
              scrollToIndex={selectedIndex >= 0 ? selectedIndex : undefined}
              width={Math.max(width, 0)}
            />
          )}
        </AutoSizer>
      </div>
    </div>
  );
};

const getOptionKey = <Option, >(option: Option): string => {
  const selectableOption = option as SelectableOption;

  if (selectableOption.value !== undefined) {
    return String(selectableOption.value);
  }

  return JSON.stringify(option);
};

const getOptionLabel = <Option, >(option: Option): string => {
  const selectableOption = option as SelectableOption;

  if (selectableOption.label !== undefined) {
    return selectableOption.label;
  }

  if (selectableOption.value !== undefined) {
    return String(selectableOption.value);
  }

  return "";
};

const isOptionDisabled = <Option, >(option: Option): boolean => {
  const selectableOption = option as SelectableOption;

  return selectableOption.isDisabled === true;
};

const filterOptionsByInput = <Option, >(
  options: Option[],
  inputValue: string,
): Option[] => {
  const normalizedInput = inputValue.trim().toLowerCase();

  if (!normalizedInput) {
    return options;
  }

  return options.filter((option) => {
    const selectableOption = option as SelectableOption;
    const searchableText = [
      getOptionLabel(option),
      selectableOption.value !== undefined
        ? String(selectableOption.value)
        : "",
    ]
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedInput);
  });
};

/**
 * Visual-only checkbox used inside react-select dropdown options.
 * Uses CheckboxPrimitive.Root appearance but is not interactive — avoids
 * the Radix internal synthetic click (from its hidden <input>) that
 * bubbles into react-select and causes an infinite update loop.
 * Selection is handled by react-select's Option component instead.
 */
const VisualCheckbox = React.memo(
  ({ checked }: { checked: boolean | "indeterminate" }): React.ReactElement => {
    const isIndeterminate = checked === "indeterminate";
    const isChecked = checked === true;
    return (
      <span
        aria-hidden="true"
        data-state={
          isIndeterminate
            ? "indeterminate"
            : isChecked
              ? "checked"
              : "unchecked"
        }
        className="peer border-gray-400 text-white data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=checked]:border-primary data-[state=indeterminate]:border-primary size-5 shrink-0 rounded-6 border inline-grid place-content-center pointer-events-none"
      >
        {isIndeterminate && <MinusIcon className="size-3.5" />}
        {isChecked && <CheckIcon className="size-3.5" />}
      </span>
    );
  },
);
VisualCheckbox.displayName = "VisualCheckbox";

const CheckboxOption = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: OptionProps<Option, IsMulti, Group>,
): React.ReactElement => {
  const isNewOption = Boolean(
    (props.data as { __isNew__?: boolean } | undefined)?.__isNew__,
  );

  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        {!isNewOption && <VisualCheckbox checked={props.isSelected} />}
        <span>{props.label}</span>
      </div>
    </components.Option>
  );
};

const SelectAllContext = React.createContext<SelectAllContextValue | null>(
  null,
);

const SelectAllMenuList = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  menuListProps: MenuListProps<Option, IsMulti, Group>,
): React.ReactElement => {
  const selectAllContext = React.useContext(SelectAllContext);

  if (!selectAllContext) {
    return <components.MenuList {...menuListProps} />;
  }

  const {
    handleSelectAll,
    isSelectAllChecked,
    isSelectAllIndeterminate,
    relevantOptionsLength,
    selectAllLabel,
  } = selectAllContext;

  return (
    <components.MenuList {...menuListProps}>
      <button
        aria-checked={
          isSelectAllIndeterminate
            ? "mixed"
            : isSelectAllChecked
              ? "true"
              : "false"
        }
        role="checkbox"
        className="flex w-full items-center gap-2 border-b border-border bg-[var(--white)] px-3 py-2 text-left text-sm font-medium text-foreground hover:bg-[var(--bg-hover)] disabled:cursor-not-allowed disabled:opacity-60"
        disabled={relevantOptionsLength === 0}
        onClick={(event): void => {
          if (event.detail === 0) {
            handleSelectAll();
          }
        }}
        onMouseDown={(event): void => {
          event.preventDefault();
          event.stopPropagation();
          handleSelectAll();
        }}
        type="button"
      >
        <VisualCheckbox
          checked={
            isSelectAllIndeterminate ? "indeterminate" : isSelectAllChecked
          }
        />
        <span>{selectAllLabel}</span>
      </button>
      {menuListProps.children}
    </components.MenuList>
  );
};

const CustomMultiselectInner = <Option, IsMulti extends boolean = false>(
  props: CustomMultiselectProps<Option, IsMulti>,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement => {
  const {
    options,
    value,
    onChange,
    onCreateOption,
    placeholder,
    className,
    labelName,
    wrapperClassName,
    required,
    isMulti,
    isDisabled,
    isCreatable,
    errors,
    handleKeyDown,
    components: selectComponents,
    menuPortalTarget,
    menuPosition,
    enableVirtualization = false,
    showSelectAll = true,
    selectAllLabel = "Select all",
    showSelectedFirst = true,
    limitVisibleValues = false,
  } = props;
  const [inputValue, setInputValue] = React.useState("");

  const Component = isCreatable ? CreatableSelect : Select;
  const canSelectAll = Boolean(isMulti && showSelectAll);
  const shouldLimitValues = Boolean(isMulti && limitVisibleValues);
  const selectedOptions = React.useMemo<MultiValue<Option>>(
    () => (Array.isArray(value) ? (value as MultiValue<Option>) : []),
    [value],
  );
  const selectedOptionKeys = React.useMemo(
    () => new Set(selectedOptions.map((option) => getOptionKey(option))),
    [selectedOptions],
  );
  const mergedOptions = React.useMemo(() => {
    if (!shouldLimitValues || !isCreatable) {
      return options;
    }

    const baseOptions = options ?? [];
    const existingKeys = new Set(
      baseOptions.map((option) => getOptionKey(option)),
    );
    const createdSelectedOptions = selectedOptions.filter(
      (option) => !existingKeys.has(getOptionKey(option)),
    );

    if (createdSelectedOptions.length === 0) {
      return options;
    }

    return [...createdSelectedOptions, ...baseOptions];
  }, [isCreatable, options, selectedOptions, shouldLimitValues]);
  const availableOptions = React.useMemo(
    () => mergedOptions?.filter((option) => !isOptionDisabled(option)) ?? [],
    [mergedOptions],
  );
  const relevantOptions = React.useMemo(
    () => filterOptionsByInput(availableOptions, inputValue),
    [availableOptions, inputValue],
  );
  const orderedOptions = React.useMemo(() => {
    if (!isMulti || !showSelectedFirst || !mergedOptions) {
      return mergedOptions;
    }

    return [
      ...mergedOptions.filter((option) =>
        selectedOptionKeys.has(getOptionKey(option)),
      ),
      ...mergedOptions.filter(
        (option) => !selectedOptionKeys.has(getOptionKey(option)),
      ),
    ];
  }, [isMulti, mergedOptions, selectedOptionKeys, showSelectedFirst]);
  const relevantOptionKeys = React.useMemo(
    () => new Set(relevantOptions.map((option) => getOptionKey(option))),
    [relevantOptions],
  );
  const selectedRelevantCount = relevantOptions.filter((option) =>
    selectedOptionKeys.has(getOptionKey(option)),
  ).length;
  const isSelectAllChecked =
    relevantOptions.length > 0 &&
    selectedRelevantCount === relevantOptions.length;
  const isSelectAllIndeterminate =
    selectedRelevantCount > 0 && selectedRelevantCount < relevantOptions.length;

  const handleSelectAll = React.useCallback((): void => {
    if (!canSelectAll || !onChange || relevantOptions.length === 0) {
      return;
    }

    const nextValue: MultiValue<Option> = isSelectAllChecked
      ? selectedOptions.filter(
          (option) => !relevantOptionKeys.has(getOptionKey(option)),
        )
      : [
          ...selectedOptions,
          ...relevantOptions.filter(
            (option) => !selectedOptionKeys.has(getOptionKey(option)),
          ),
        ];

    onChange(
      nextValue as unknown as IsMulti extends true ? MultiValue<Option> : never,
      {
        action: isSelectAllChecked ? "deselect-option" : "select-option",
      } as ActionMeta<Option>,
    );
  }, [
    canSelectAll,
    isSelectAllChecked,
    onChange,
    relevantOptionKeys,
    relevantOptions,
    selectedOptionKeys,
    selectedOptions,
  ]);

  const handleInputChange = React.useCallback(
    (newValue: string, actionMeta: InputActionMeta): string => {
      if (actionMeta.action === "input-change") {
        setInputValue(newValue);
      }

      if (actionMeta.action === "set-value") {
        setInputValue("");
      }

      if (actionMeta.action === "menu-close") {
        setInputValue("");
      }

      return newValue;
    },
    [],
  );

  const selectAllContextValue = React.useMemo<SelectAllContextValue>(
    () => ({
      handleSelectAll,
      isSelectAllChecked,
      isSelectAllIndeterminate,
      relevantOptionsLength: relevantOptions.length,
      selectAllLabel,
    }),
    [
      handleSelectAll,
      isSelectAllChecked,
      isSelectAllIndeterminate,
      relevantOptions.length,
      selectAllLabel,
    ],
  );

  const customStyles: StylesConfig<Option, IsMulti> = {
    container: (base) => ({
      ...base,
      minWidth: 0,
      maxWidth: "100%",
    }),
    control: (base, state) => ({
      ...base,
      minWidth: 0,
      maxWidth: "100%",
      ...(shouldLimitValues ? { height: 40 } : {}),
      ...(isMulti && state.isDisabled ? { pointerEvents: "auto" } : {}),
    }),
    valueContainer: (base) => ({
      ...base,
      ...(isMulti ? { minWidth: 0, maxWidth: "100%" } : {}),
      ...(shouldLimitValues
        ? { display: "flex", flexWrap: "nowrap", height: 38 }
        : {}),
    }),
    indicatorsContainer: (base, state) => ({
      ...base,
      ...(isMulti && state.isDisabled ? { pointerEvents: "none" } : {}),
    }),
    multiValue: (base) => ({
      ...base,
      minWidth: 0,
      flexShrink: 1,
      maxWidth: "100%",
    }),
    multiValueRemove: (base, state) => ({
      ...base,
      flexShrink: 0,
      ...(state.selectProps.isDisabled ? { pointerEvents: "none" } : {}),
    }),
    option: (provided, state) => {
      const data = state.data as {
        isActive?: boolean;
        isDisabled?: boolean;
      };

      const isInactiveOption = data.isActive === false;
      const isDisabledOption = data.isDisabled === true;
      const optionBackgroundColor =
        state.isFocused || state.isSelected
          ? "var(--bg-hover)"
          : "var(--white)";

      return {
        ...provided,
        backgroundColor:
          isInactiveOption && state.isFocused
            ? "transparent"
            : optionBackgroundColor,
        color: "var(--foreground)",

        // Inactive option (like .inactive-option)
        borderLeft: isInactiveOption
          ? "3px solid var(--color-destructive)"
          : undefined,

        //  Disabled option (like .disable-option)
        opacity: isDisabledOption ? 0.6 : provided.opacity,
        pointerEvents: isDisabledOption ? "none" : provided.pointerEvents,
        cursor: isDisabledOption ? "not-allowed" : provided.cursor,
        border: isDisabledOption ? "0" : provided.border,
      };
    },
    menuPortal: (base) => ({
      ...base,
      zIndex: 999,
      pointerEvents: "auto",
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: 200,
      overflowY: enableVirtualization ? "hidden" : "auto",
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--foreground)",
    }),
    input: (base) => ({
      ...base,
      color: "var(--foreground)",
      ...(isMulti ? { minWidth: 0, maxWidth: "100%" } : {}),
      ...(shouldLimitValues
        ? {
            minWidth: MIN_MULTISELECT_INPUT_WIDTH,
            flex: `1 0 ${MIN_MULTISELECT_INPUT_WIDTH}px`,
          }
        : {}),
    }),
  };

  const mergedComponents = React.useMemo(
    () => ({
      ...(shouldLimitValues
        ? {
            ValueContainer: LimitedValueContainer,
          }
        : {}),
      MultiValueLabel: TruncatedMultiValueLabel,
      ...(enableVirtualization && !canSelectAll
        ? {
            MenuList: VirtualizedMenuList,
          }
        : {}),
      ...(canSelectAll || shouldLimitValues
        ? {
            Option: CheckboxOption,
          }
        : {}),
      ...(canSelectAll
        ? {
            MenuList: SelectAllMenuList,
          }
        : {}),
      ...selectComponents,
    }),
    [canSelectAll, enableVirtualization, selectComponents, shouldLimitValues],
  );

  return (
    <div
      ref={ref}
      className={`custom-multiselect-wrapper min-w-0 max-w-full ${isMulti && !shouldLimitValues ? "scrollable-select " : ""}${wrapperClassName ?? ""}`}
    >
      {labelName && (
        <Label className="mb-1.5">
          {labelName}
          {required && <span className="text-destructive -ml-1">*</span>}
        </Label>
      )}

      <SelectAllContext.Provider value={selectAllContextValue}>
        <Component<Option, IsMulti>
          isMulti={isMulti}
          isDisabled={isDisabled}
          options={orderedOptions}
          value={value}
          onChange={onChange}
          {...(isCreatable && onCreateOption ? { onCreateOption } : undefined)}
          placeholder={placeholder}
          className={`custom-multiselect ${className ?? ""}`}
          classNamePrefix="custom-multiselect"
          onKeyDown={handleKeyDown}
          onInputChange={handleInputChange}
          styles={customStyles}
          isOptionDisabled={isOptionDisabled}
          components={mergedComponents}
          closeMenuOnSelect={canSelectAll ? false : undefined}
          hideSelectedOptions={
            canSelectAll || shouldLimitValues ? false : undefined
          }
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          closeMenuOnScroll={false} // Prevents menu from closing when parent scrolls
          captureMenuScroll={false} // Stops React Select from hijacking the scroll event
          menuShouldScrollIntoView={true}
        />
      </SelectAllContext.Provider>
      {errors && <p className="mt-1 text-sm text-red-500">{errors}</p>}
    </div>
  );
};

const CustomMultiselect = forwardRef(CustomMultiselectInner) as <
  Option,
  IsMulti extends boolean = false,
>(
  props: CustomMultiselectProps<Option, IsMulti> & {
    ref?: React.Ref<HTMLDivElement>;
  },
) => React.ReactElement;

export default CustomMultiselect;
